import styled, { css } from 'styled-components';
import { motion } from 'motion/react';
import NextLink from 'next/link';
import { BodySmReg, BodyMedReg, FrostedGlassStrong } from '@seed-health/tokens';
import { mqMediumMin, mqLargeMin } from 'styled/tools/media-queries';
import type { ButtonSize } from '.';

const svgColorInherit = css`
  fill: currentColor;
  stroke: currentColor;
  color: currentColor;
  * {
    color: currentColor;
  }
`;

// Transitions

const fillTransition = css`
  transition:
    opacity 0.2s ease-in-out,
    background-color 0.2s ease-in-out;
`;

const outlineTransition = css`
  transition:
    opacity 0.2s ease-in-out,
    border-color 0.2s ease-in-out;
`;

// Variant styles

// Each variant restates its color on :hover/:active/:focus — the global
// a:focus / a:hover rules in components/styles/elements.tsx otherwise
// out-rank the base class color when Button renders as a link.
const primaryStyles = css`
  background-color: var(--actions-primary);
  color: var(--actions-inverse);
  border: none;
  ${fillTransition}

  &:hover, &:active, &:focus {
    color: var(--actions-inverse);
  }
  &:focus-visible {
    outline: var(--stroke-default) solid var(--actions-primary);
  }
`;

const lightStyles = css`
  background-color: var(--actions-inverse);
  color: var(--actions-primary);
  border: none;
  ${fillTransition}

  &:hover, &:active, &:focus {
    color: var(--actions-primary);
  }
  &:focus-visible {
    outline: var(--stroke-default) solid var(--actions-inverse);
  }
`;

const glassStyles = css`
  background-color: var(--scrim-glass-dark-35);
  color: var(--text-inverse-primary);
  border: none;
  backdrop-filter: ${FrostedGlassStrong};
  -webkit-backdrop-filter: ${FrostedGlassStrong};
  ${fillTransition}

  &:hover, &:active, &:focus {
    color: var(--text-inverse-primary);
  }
  &:focus-visible {
    outline: var(--stroke-default) solid var(--scrim-glass-dark-35);
  }
`;

const glassLightStyles = css`
  background-color: var(--scrim-glass-dark-08);
  color: var(--text-primary);
  border: none;
  backdrop-filter: ${FrostedGlassStrong};
  -webkit-backdrop-filter: ${FrostedGlassStrong};
  ${fillTransition}

  &:hover, &:active, &:focus {
    color: var(--text-primary);
  }
  &:focus-visible {
    outline: var(--stroke-default) solid var(--scrim-glass-light-20);
  }
`;

const outlineDarkStyles = css`
  background-color: transparent;
  color: var(--actions-primary);
  border: var(--stroke-default) solid var(--actions-primary);
  ${outlineTransition}

  &:hover {
    background-color: var(--state-layer-dark-hovered);
  }
  &:active {
    background-color: var(--state-layer-dark-pressed);
  }
  &:hover,
  &:active,
  &:focus {
    color: var(--actions-primary);
  }
  &:focus-visible {
    outline: var(--stroke-default) solid var(--actions-inverse);
    background-color: var(--state-layer-dark-focused);
  }
`;

const outlineLightStyles = css`
  background-color: transparent;
  color: var(--actions-inverse);
  border: var(--stroke-default) solid var(--actions-inverse);
  ${outlineTransition}

  &:hover {
    background-color: var(--state-layer-light-hovered);
  }
  &:active {
    background-color: var(--state-layer-light-pressed);
  }
  &:hover,
  &:active,
  &:focus {
    color: var(--actions-inverse);
  }
  &:focus-visible {
    outline: var(--stroke-default) solid var(--actions-primary);
    background-color: var(--state-layer-light-focused);
  }
`;

// Size styles

const sizeStyles: Record<ButtonSize, ReturnType<typeof css>> = {
  sm: css`
    ${BodySmReg}
    height: 36px;
    padding: var(--spacing-8) var(--spacing-16);
    gap: var(--spacing-4);
  `,
  md: css`
    ${BodyMedReg}
    height: 48px;
    padding: var(--spacing-16) var(--spacing-24);
    gap: var(--spacing-8);
  `,
};

// Base

interface StyledButtonProps {
  $light?: boolean;
  $glass?: boolean;
  $outline?: boolean;
  $sizeBase: ButtonSize;
  $sizeTablet?: ButtonSize;
  $sizeDesktop?: ButtonSize;
  $animate?: boolean;
}

const buttonBase = css<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 140%;
  letter-spacing: -0.16px;
  border-radius: var(--radius-full);
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  width: fit-content;

  svg {
    ${svgColorInherit}
  }

  /* Doubled class so an ancestor's \`* { color }\` (e.g. the DS-01 Hero) can't
     tie the variant color and win on stylesheet order. */
  && {
    ${({ $glass, $light, $outline }) => {
      if ($outline && $light) return outlineLightStyles;
      if ($outline) return outlineDarkStyles;
      if ($glass && $light) return glassLightStyles;
      if ($glass) return glassStyles;
      if ($light) return lightStyles;
      return primaryStyles;
    }}
  }

  ${({ $sizeBase }) => sizeStyles[$sizeBase]}

  ${({ $sizeTablet }) =>
    $sizeTablet &&
    css`
      @media ${mqMediumMin} {
        ${sizeStyles[$sizeTablet]}
      }
    `}

  ${({ $sizeDesktop }) =>
    $sizeDesktop &&
    css`
      @media ${mqLargeMin} {
        ${sizeStyles[$sizeDesktop]}
      }
    `}

  &:hover {
    opacity: var(--hover);
  }
  &:active {
    opacity: var(--active);
  }

  &:focus-visible {
    opacity: var(--focused);
    outline-offset: 1px;
    outline-style: solid;
  }

  &:disabled {
    opacity: var(--disabled);
    cursor: not-allowed;
    pointer-events: none;
  }

  ${({ $animate }) =>
    $animate &&
    css`
      gap: 0;
    `}
`;

// Exports

export const IconWrapper = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  color: inherit;

  svg {
    ${svgColorInherit}
  }
`;

export const ButtonWrapper = styled(motion.button)<StyledButtonProps>`
  ${buttonBase}
`;

export const LinkWrapper = styled(motion(NextLink))<StyledButtonProps>`
  ${buttonBase}
`;
